import { Helmet } from "react-helmet-async";
import { useSettings } from "@/contexts/SettingsContext";
import { resolveImage } from "@/lib/api";

/**
 * Reusable SEO component. Sets <title>, meta description, canonical, OG, Twitter,
 * and a default Organization + LocalBusiness JSON-LD on every page.
 *
 * Props:
 *  - title: page-specific title (will be wrapped in template)
 *  - description: page meta description (overrides settings.meta_description)
 *  - image: OG image URL
 *  - path: canonical path (e.g. "/services")
 *  - type: og:type (default "website", "article" for product detail)
 *  - schema: extra JSON-LD object to include alongside Organization
 *  - noTemplate: when true, use title as-is (e.g. for the home page)
 */
export default function Seo({ title, description, image, path = "", type = "website", schema, noTemplate = false }) {
  const { settings } = useSettings();
  if (!settings) return null;

  const baseTitle = settings.site_title || "OneTec";
  const template = settings.site_title_template || "{page} — OneTec";
  const finalTitle = !title || noTemplate ? (title || baseTitle) : template.replace("{page}", title);
  const finalDesc = description || settings.meta_description || "";
  const ogImage = image
    ? resolveImage(image)
    : (settings.hero_image_url ? resolveImage(settings.hero_image_url) : "");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `${origin}${path || "/"}`.replace(/\/+$/, "") || origin;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "OneTec",
    legalName: "OneTec SRL",
    url: origin,
    logo: settings.nav_logo_url ? resolveImage(settings.nav_logo_url) : undefined,
    foundingDate: String(settings.founding_year || 2002),
    description: settings.meta_description,
    sameAs: [
      "https://www.onetec.eu",
      "https://www.linkedin.com/company/onetec-eventattitude/",
      "https://www.youtube.com/@OneTecGroup",
      "https://www.instagram.com/onetec_eventattitude/",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: settings.contact_phone || "+32 2 702 36 36",
      contactType: "sales",
      email: settings.contact_email || "hello@onetec.eu",
      areaServed: "EU",
      availableLanguage: ["en", "fr", "nl"],
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "OneTec SRL",
    image: settings.nav_logo_url ? resolveImage(settings.nav_logo_url) : undefined,
    "@id": origin,
    url: origin,
    telephone: settings.contact_phone || "+32 2 702 36 36",
    email: settings.contact_email || "hello@onetec.eu",
    priceRange: "€€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Tollaan 75",
      addressLocality: "Zaventem",
      postalCode: "1932",
      addressCountry: "BE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 50.8849,
      longitude: 4.4738,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "18:00",
      },
    ],
    vatID: settings.vat || "BE0478.889.493",
    areaServed: [
      { "@type": "Country", name: "Belgium" },
      { "@type": "Country", name: "France" },
      { "@type": "Country", name: "Netherlands" },
      { "@type": "Country", name: "Luxembourg" },
      { "@type": "Country", name: "Germany" },
      { "@type": "Country", name: "United Kingdom" },
    ],
  };

  return (
    <Helmet>
      <html lang="en" />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="OneTec" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={`OneTec — ${title || "Eventattitude"}`} />}
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <meta name="twitter:image:alt" content={`OneTec — ${title || "Eventattitude"}`} />}

      {/* JSON-LD: Organization + LocalBusiness on every page (great for GEO/local SEO) */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}
